import { useEffect, useMemo, useRef, useState } from 'react'

import { CalendarOutlined, CloudUploadOutlined, DeploymentUnitOutlined, FileOutlined, FilePdfOutlined, PictureOutlined, PlusOutlined, VideoCameraOutlined } from '@ant-design/icons'
import { NodeTypes } from '@cloudike/web_files'
import { systemFieldsSelector } from "features/assets/model/selectors/assetsSelectors"
import {
  assetsActions,
  deleteAssetsFieldThunk,
  fetchSystemFieldsThunk,
  renameAssetByIdThunk,
  setFieldOnAssetThunk,
  updateAssetThunk,
  updateSystemFieldThunk
} from "features/assets/model/slice/assetsSlice"
import { useOnClickTag } from "features/common/hooks/useOnClickTag"
import {
  createNewSystemTagThunk,
  updateBoardDescriptionThunk
} from 'features/public-link-files/model/slice/publicLinkFilesSlice'
import { MemberRole } from "features/team/model/types/members"
import { getUserRole, isAuthSelector, userSelector } from 'features/user/model/selectors/userSelectors'
import moment from "moment"
import { useGetEnablePermission } from "features/public-link-files/hooks/useGetEnablePermission"
import { useAppDispatch, useAppSelector } from "shared/config/store"
import { EditableContent, Switch, Tag } from 'shared/ui'
import { StatusSelector } from "shared/ui/StatusSelector"
import { TagsSelector } from "shared/ui/TagsSelector"
import { hasAccess } from "shared/utils/access"
import { toMb } from "shared/utils/filesUtils"
import { css, styled } from "styled-components"
import {
  getFilesPreviewAiTagsSelector, getFilesPreviewFieldsIdsSelector,
  getFilesPreviewIsPublicLinkSelector,
  getFilesPreviewItemsSelector,
  getFilesPreviewSystemFieldsOptionsSelector,
  getFilesPreviewSystemFieldsValuesSelector,
  getFilesPreviewTabsContentLoadedSelector
} from 'features/files-preview/model/selectors/filesPreviewSelectors'
import {
  changePublicAssetStatusThunk,
  changePublicAssetTagsThunk,
  filesPreviewActions,
  updateAssetDescriptionThunk,
  updateAssetNameThunk
} from 'features/files-preview/model/slice/filesPreviewSlice'
import { useTranslation } from 'react-i18next'
import { NOTIFICATION_TYPES, showNotification } from 'features/common/notifications'
import { FILES_EXTANSIONS_FOR_AI_ASSISTANT_INDEXING } from 'features/assets/model/types/assets'
import { aiAssistantSlice, updateAssetAiAssistantUseForRagFieldThunk } from 'features/ai-assistant/model'

import {
  getDescriptionFieldId,
  getFilesPublicLinkNodeDataSelector,
} from '../../public-link-files/selectors/publicLinkFilesSelectors'
import { useBoardFields } from '../../boards/hooks'

import { TabContentLoader } from "./TabContentLoader"


const tagsMock = []

for (let i = 10; i < 36; i++) {
  const value = i.toString(36) + i

  tagsMock.push(value)
}

const statusesColors = {
  'Waiting for approval': 'blue',
  'Ready to use': 'green',
  'In progress': 'volcano',
  'Remove status': 'red',
}

const normalizeDescriptionValue = (raw: unknown): string => {
  if (typeof raw === 'string') {
    return raw
  }

  if (raw && typeof raw === 'object') {
    const valueProp = (raw as any).value

    if (typeof valueProp === 'string') {
      return valueProp
    }

    if (valueProp && typeof valueProp === 'object') {
      if (typeof valueProp.text === 'string') {
        return valueProp.text
      }

      if (typeof valueProp.html === 'string') {
        return valueProp.html
      }
    }

    if (typeof (raw as any).text === 'string') {
      return (raw as any).text
    }
  }

  return ''
}

const sanitizeDescription = (value: string | null | undefined): string => {
  if (typeof value !== 'string') {
    return ''
  }

  return value.trim().length > 0 ? value : ''
}

export const InfoTabContent = ({ currentIndex,  }: { currentIndex: number }) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const isAuth = useAppSelector(isAuthSelector)
  const user = useAppSelector(userSelector)
  const items = useAppSelector(getFilesPreviewItemsSelector)
  const aiTags = useAppSelector(getFilesPreviewAiTagsSelector)
  const systemFields = useAppSelector(systemFieldsSelector)
  const systemFieldsOptions = useAppSelector(getFilesPreviewSystemFieldsOptionsSelector)
  const systemFieldsValues = useAppSelector(getFilesPreviewSystemFieldsValuesSelector)
  const fieldsIds = useAppSelector(getFilesPreviewFieldsIdsSelector)
  const contentLoaded = useAppSelector(getFilesPreviewTabsContentLoadedSelector)
  const userRole = useAppSelector(getUserRole)
  const isPublicLink = useAppSelector(getFilesPreviewIsPublicLinkSelector)
  const publicDescriptionFieldId = useAppSelector(getDescriptionFieldId)
  const { isEditContentEnabled } = useGetEnablePermission()
  const { handleAiTagClick } = useOnClickTag()
  const userId = useMemo(() => isAuth ? user?.id : 0, [user])
  const abortControllerRef = useRef(null)

  let nodeData = null
  let shareId = null
  let statusFieldId = null
  let systemTags = null
  let tagsFieldId = null
  let boardTags = null
  let descriptionFieldId: string | null = null

  if(isPublicLink) {
    nodeData = useAppSelector(getFilesPublicLinkNodeDataSelector)
    systemTags = systemFieldsOptions?.tags
    tagsFieldId = fieldsIds.tagsFieldId
    statusFieldId = fieldsIds.statusFieldId
    boardTags = systemFieldsValues?.tags
    shareId = nodeData?.id
    descriptionFieldId = publicDescriptionFieldId
  }

  if (!isPublicLink) {
    descriptionFieldId = fieldsIds.descriptionFieldId
  }

  const statusField = systemFields?.find((field) => field.title === 'status')
  const statusFieldOptions = !isPublicLink ? (statusField?.options || []) : (systemFieldsOptions?.status || [])
  const statuses = [...statusFieldOptions, systemFieldsValues?.status ? {
    key: 'remove-status',
    value: 'Remove status',
    color: 'red',
    borderColor: 'transparent',
    border: 'none',
  } : null].filter(Boolean)
  const statusesOptions = statuses?.map((status) => ({
    label: status.value,
    value: status.id,
    key: status.id || 'remove-status',
    color: statusesColors[status.value],
  })) || []
  const currentStatus = statusesOptions?.find((status) => status.value === systemFieldsValues?.status)

  const tagsField = systemFields?.find((field) => field.title === 'tags')
  const tags = !isPublicLink ? (tagsField?.options || []) : (systemFieldsOptions?.tags || [])
  const tagsOptions = tags?.map((tag) => ({
    label: tag.value,
    value: tag.id,
    id: tag.id,
  })).filter((tag) => {
    return !(systemFieldsValues?.tags || []).includes(tag.id)
  }) || []
  const tagsOptionsWithIds = (systemFieldsValues?.tags || []).map((tag) => ({
    label: tags?.find((t) => t.id === tag)?.value,
    id: tag,
    value: tag,
  })) || []

  const useForRagField = systemFields?.find((field) => field.title === 'use_for_rag')

  const currentItem = items[currentIndex] as any
  const isBoardItem = currentItem?.type === NodeTypes.DIR || currentItem?.type === 'dir'

  const descriptionFromFields = sanitizeDescription(normalizeDescriptionValue(systemFieldsValues?.description))
  const descriptionFromItem = sanitizeDescription(normalizeDescriptionValue(currentItem?.description))
  const initialDescription = descriptionFromFields || descriptionFromItem

  const { updateField: updateBoardField } = useBoardFields(currentItem?.id || '')

  const [titleValue, setTitleValue] = useState(currentItem?.name)
  const [descriptionValue, setDescriptionValue] = useState(initialDescription)

  useEffect(() => {
    setTitleValue(currentItem?.name)
    const nextDescriptionFromFields = sanitizeDescription(normalizeDescriptionValue(systemFieldsValues?.description))
    const nextDescriptionFromItem = sanitizeDescription(normalizeDescriptionValue(currentItem?.description))
    setDescriptionValue(nextDescriptionFromFields || nextDescriptionFromItem)
  }, [currentItem?.id, currentItem?.description, currentItem?.name, systemFieldsValues?.description])

  const handleSetTags = (tags: string[]) => {
    if (isPublicLink) {

      dispatch(changePublicAssetTagsThunk({
        userId,
        shareId,
        nodeId: currentItem.id,
        fieldID: tagsFieldId,
        data: { value: tags }
      }))

      // Update local state
      dispatch(filesPreviewActions.updateSystemFieldsValues({ tags }))
      return
    }

    // Handle tags change for regular assets
    const successCallback = () => {
      dispatch(filesPreviewActions.updateSystemFieldsValues({ tags }))
    }

    dispatch(setFieldOnAssetThunk({ id: currentItem.id, fieldID: tagsField?.id, value: tags, successCallback }))
  }

  const handleCreateTag = (value: string) => {
    abortControllerRef.current = new AbortController()

    if (isPublicLink) {
      // Handle creating new tag for public link assets
      const currentBoardData = {
        node_id: currentItem.id,
        type: 'file',
        ...currentItem
      }
      dispatch(createNewSystemTagThunk({
        currentBoardData,
        fieldID: tagsFieldId,
        systemTags,
        boardTags,
        nodeId: currentItem.id,
        newTagValue: value,
        signal: new AbortController().signal
      })).unwrap()
        .finally(() => {
          abortControllerRef.current = null
        })
      return
    }

    // Handle creating new tag for regular assets
    const successCallback = (newTags: any) => {
      // setCurrentTags([...tags, searchValue])
      dispatch(fetchSystemFieldsThunk())

      const tagId = newTags.options.find((tag: any) => tag.value === value)?.id
      handleSetTags([...systemFieldsValues.tags, tagId])
    }
    dispatch(updateSystemFieldThunk({ fieldID: tagsField?.id, data: { options: [...tags, { value }] }, successCallback }))
  }

  const handleRenameAsset = (name: string) => {
    const callback = () => {
      dispatch(filesPreviewActions.updateItem({ id: currentItem.id, name }))
      dispatch(assetsActions.updateAsset({ id: currentItem.id, name }))
    }

    if (isPublicLink) {
      dispatch(updateAssetNameThunk({ userId, nodeId: currentItem.id, shareId, name, callback }))
      return
    }

    dispatch(renameAssetByIdThunk({ id: currentItem.id, name, callback }))
  }

  const handleSetDescription = (description: string) => {
    const nextDescription = sanitizeDescription(description)

    setDescriptionValue(nextDescription)

    const applySuccess = () => {
      dispatch(filesPreviewActions.updateItem({ id: currentItem.id, description: nextDescription }))
      if (!isBoardItem) {
        dispatch(assetsActions.updateAsset({ id: currentItem.id, description: nextDescription }))
      }
      dispatch(filesPreviewActions.updateSystemFieldsValues({ description: nextDescription }))
    }

    if (isPublicLink) {
      if (isBoardItem) {
        dispatch(updateBoardDescriptionThunk({
          userId,
          nodeId: currentItem.id,
          shareId,
          fieldID: descriptionFieldId ?? null,
          description: nextDescription
        }))
          .unwrap()
          .then(applySuccess)
          .catch(() => null)
        return
      }

      dispatch(updateAssetDescriptionThunk({ userId, nodeId: currentItem.id, shareId, fieldID: descriptionFieldId ?? null, description: nextDescription, callback: applySuccess }))
      return
    }

    if (isBoardItem) {
      updateBoardField('description', nextDescription || '')
        .then(applySuccess)
        .catch(() => null)
      return
    }

    if (descriptionFieldId) {
      dispatch(setFieldOnAssetThunk({
        id: currentItem.id,
        fieldID: descriptionFieldId,
        value: nextDescription,
        successCallback: applySuccess
      }))
        .unwrap()
        .catch(() => null)
      return
    }

    dispatch(updateAssetThunk({ id: currentItem.id, data: { description: nextDescription }, successCallback: applySuccess }))
  }

  const handleSetStatus = (status) => {
    if (isPublicLink) {
      const statusId = status.key === 'remove-status' ? null : status.value

      dispatch(changePublicAssetStatusThunk({
        userId,
        shareId,
        nodeId: currentItem.id,
        fieldID: statusFieldId,
        statusId
      }))

      // Update local state
      dispatch(filesPreviewActions.updateSystemFieldsValues({
        status: statusId
      }))
      return
    }

    // Handle status change for regular assets
    if (status.key === 'remove-status') {
      dispatch(deleteAssetsFieldThunk({ nodeId: currentItem.id, fieldID: statusField?.id, signal: new AbortController().signal }))
      dispatch(filesPreviewActions.updateSystemFieldsValues({ status: null }))
      return
    }

    const successCallback = () => {
      dispatch(filesPreviewActions.updateSystemFieldsValues({ status: status.value }))
    }

    dispatch(setFieldOnAssetThunk({ id: currentItem.id, fieldID: statusField?.id, value: status.value, successCallback }))
  }

  const handleSetUseForRag = (useForRag: boolean) => {
    const successCallback = () => {
      dispatch(filesPreviewActions.updateSystemFieldsValues({ use_for_rag: useForRag }))
      dispatch(aiAssistantSlice.actions.setItemsMarkedAsUsefullForAiAssistant({ nodeId: currentItem.id, useForRagFieldValue: useForRag }))

      showNotification({
        type: NOTIFICATION_TYPES.SUCCESS,
        title: useForRag ? t('l_notification_aivardAllowSuccess') : t('l_notification_aivardHideSuccess'),
        message: '',
      })
    }

    dispatch(setFieldOnAssetThunk({ id: currentItem.id, fieldID: useForRagField?.id, value: useForRag, successCallback }))
  }


  if (!contentLoaded) {
    return <TabContentLoader />
  }

  const fileInfo = currentItem?.file_info
  const metaData = fileInfo?.meta_data

  const checkDisabled = ({ permissionEnabled, acsessRoles, isPublicLink }: {permissionEnabled: boolean, acsessRoles: MemberRole[], isPublicLink: boolean}) => {
    return isPublicLink ? !permissionEnabled : !hasAccess(userRole, acsessRoles)
  }

  const getTagsMode = (): 'select-create' | 'select-only' => {
    return isPublicLink ? 'select-only' : 'select-create'
  }

  return (
    <SInfoTabWrapper>
      <SInfoTabContent>
        <SStatusSelectorContainer>
          <SStatusSelector
            showBorder={false}
            status={currentStatus}
            statuses={statusesOptions}
            onChangeStatus={handleSetStatus}
            disabled={checkDisabled({ permissionEnabled: isEditContentEnabled, acsessRoles: [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER], isPublicLink })}
            defaultStatusConfig={{
              borderColor: '#424242',
              backgroundColor: 'transparent',
              color: '#FFFFFFD9',
              label: 'Add status',
              fontSize: '12px',
              padding: '0 8px',
              height: '22px',
            }}
          />
        </SStatusSelectorContainer>

        <STitleEditableContent
          value={titleValue}
          onChange={setTitleValue}
          onBlur={handleRenameAsset}
          placeholder="Add title"
          singleLine={true}
          autoSelectContent={true}
          disabled={checkDisabled({ permissionEnabled: isEditContentEnabled, acsessRoles: [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER], isPublicLink })}
        />

        <SEditableContent
          value={descriptionValue}
          onChange={setDescriptionValue}
          onBlur={handleSetDescription}
          placeholderColor="#FFFFFFA6"
          placeholder="Click to add a description"
          disabled={checkDisabled({ permissionEnabled: isEditContentEnabled, acsessRoles: [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER], isPublicLink })}
        />

        <SMainContent>
          <SSelectLabel>
          Tags
          </SSelectLabel>

          <STagsSelector
            isPublicLink={isPublicLink}
            mode={getTagsMode()}
            options={tagsOptions}
            classNameTagsContainer="tags-selector-container"
            value={tagsOptionsWithIds}
            onSelect={handleSetTags}
            onChange={handleSetTags}
            onCreateTag={handleCreateTag}
            addTagBtnStyle={{
              color: '#FFFFFF73',
            }}
            disabled={checkDisabled({ permissionEnabled: isEditContentEnabled, acsessRoles: [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER], isPublicLink })}
            renderCustomPlaceholderContent={(onClick) => (
              <Tag
                bordered={true}
                style={{
                  margin: 0,
                  height: '22px',
                  lineHeight: '22px',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  borderColor: '#424242',
                  backgroundColor: 'transparent',
                  color: '#FFFFFFD9',
                  fontSize: '12px',
                  padding: '0 8px',
                }}
                onClick={onClick}
              >
                <PlusOutlined style={{ fontSize: '13px' }} />

              Add tags
              </Tag>
            )}
          />

          {aiTags.length > 0 && (
            <>
              <SSelectLabel className="mt-32">
              AI Tags
              </SSelectLabel>

              <SAiTags>
                {aiTags.map((tag) => (
                  <SAiTag key={tag}
                    onClick={() => {
                      handleAiTagClick(tag)
                      dispatch(filesPreviewActions.resetState())
                    }}
                  >
                    <SAiImg src={'/icons/ai-white.svg'} />

                    {tag}
                  </SAiTag>
                ))}
              </SAiTags>
            </>
          )}

          <SInfoContent>
            <SInfoRow>
              <SCalendarIcon />

              <SInfoRowRight>
                <SInfoRowTitle>
                  {moment(currentItem?.created).format("MMM DD, YYYY")}
                </SInfoRowTitle>

                <SInfoRowValue>
Created
                </SInfoRowValue>
              </SInfoRowRight>
            </SInfoRow>

            <SInfoRow>
              {fileInfo?.mime?.startsWith('image') ? (
                <SImageIcon />
              ) : fileInfo?.mime?.startsWith('video') ? (
                <SVideoIcon />
              ) : fileInfo?.mime?.startsWith('application/pdf') ? (
                <SPdfIcon />
              ) : (
                <SFileIcon />
              )}

              <SInfoRowRight>
                <SInfoRowTitle>
                  {fileInfo?.mime}

                </SInfoRowTitle>

                <SInfoRowValue>
                  {toMb(fileInfo?.size)}

                  <p>
                    {metaData?.image && `size: ${metaData?.image?.width} x ${metaData?.image?.height} px`}
                  </p>

                  <p>
                    {metaData?.video && `size: ${metaData?.video?.width} x ${metaData?.video?.height} px`}
                  </p>

                  <p>
                    {metaData?.video && `video codec: ${metaData?.video?.video_codec_name}`}
                  </p>

                  <p>
                    {metaData?.video && `audio codec: ${metaData?.video?.audio_codec_name}`}
                  </p>

                </SInfoRowValue>
              </SInfoRowRight>
            </SInfoRow>

            <SInfoRow>
              <SUploadIcon />

              <SInfoRowRight>
                <SInfoRowTitle>
                  {moment(currentItem?.updated).format("MMM DD, YYYY")}
                </SInfoRowTitle>

                <SInfoRowValue>
            Updated
                </SInfoRowValue>
              </SInfoRowRight>
            </SInfoRow>
          </SInfoContent>
        </SMainContent>
      </SInfoTabContent>

      {!isPublicLink && FILES_EXTANSIONS_FOR_AI_ASSISTANT_INDEXING.includes(currentItem?.ext) && (
        <SAllowForAiAssistantBox>
          <SAllowForAiAssistantIcon />

          <SAllowForAiAssistantTextBox>
            <SAllowForAiAssistantTitle>
              {t('a_common_allowAivard')}
            </SAllowForAiAssistantTitle>

            <SAllowForAiAssistantValue>
              {t('l_common_availableForAivard')}
            </SAllowForAiAssistantValue>
          </SAllowForAiAssistantTextBox>

          <SAllowForAiAssistantSwitch size="small"
            checked={systemFieldsValues?.use_for_rag}
            onChange={handleSetUseForRag}
          />
        </SAllowForAiAssistantBox>
      )}
    </SInfoTabWrapper>
  )
}

const SInfoTabWrapper = styled.div`
  height: calc(100vh - 150px);
  display: flex;
  flex-direction: column;
  min-height: 0;
`

const SInfoTabContent = styled.div`
  padding: 24px 24px 24px 12px;
  overflow-y: auto;
  min-height: 0;
`

const SMainContent = styled.div`
  padding-left: 12px;
`

const SInfoRow = styled.div`
  margin-bottom: 24px;
  display: flex;
  gap: 15px;
`

const SInfoRowRight = styled.div``

const SInfoRowTitle = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.85);
`

const SInfoRowValue = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.65);
`

const iconStyles = css`
  height: 30px;
  color: rgba(255, 255, 255, 0.45);
  font-size: 24px;
`

const SCalendarIcon = styled(CalendarOutlined)`
  ${iconStyles}
`

const SImageIcon = styled(PictureOutlined)`
  ${iconStyles}
`

const SVideoIcon = styled(VideoCameraOutlined)`
  ${iconStyles}
`

const SPdfIcon = styled(FilePdfOutlined)`
  ${iconStyles}
`

const SFileIcon = styled(FileOutlined)`
  ${iconStyles}
`

const SUploadIcon = styled(CloudUploadOutlined)`
  ${iconStyles}
`

const SSelectLabel = styled.div`
  margin-top: 20px;
  margin-bottom: 12px;
  font-size: 14px;
  font-weight: 400;
  color: #FFFFFFA6;

  &.mt-32 {
    margin-top: 32px;
  }
`

const SInfoContent = styled.div`
  margin-top: 40px;
  margin-bottom: 32px;
`

const SAiTags = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`

const SAiTag = styled.div`
  background: #FFFFFF0A;
  border: 1px solid #424242;
  color: #FFFFFFD9;
  font-size: 12px;
  font-weight: 400;
  height: 20px;
  display: flex;
  align-items: center;
  padding: 0 8px;
  border-radius: 4px;

  &:hover{
    cursor: pointer;
  }
`

const SAiImg = styled.img`
  margin-right: 4px;
  width: 14px;
  height: 14px;
`


const SEditableContent = styled(EditableContent)`
  margin-top: 4px;
  color: white;
  font-size: 14px;
  font-weight: 400;
  padding: 4px 11px;
  
  &:not([contenteditable="true"]) {
    border-color: transparent;
    box-shadow: none;
    cursor: pointer;
  }
`

const STitleEditableContent = styled(EditableContent)`
  margin-top: 13px;
  max-width: 100%;
  padding: 4px 11px;
  
  &:not([contenteditable="true"]) {
    border-color: transparent;
    box-shadow: none;
    cursor: pointer;
  }
  
  font-size: 16px;
  font-weight: 400;
  letter-spacing: 0.02em;
  color: white;
  
  &:focus {
    border-color: var(--color-primary);
    box-shadow: 0 0 0 2px rgba(135, 71, 223, 0.10);
  }
`

const SStatusSelectorContainer = styled.div`
  margin-left: 8px;

`

const SStatusSelector = styled(StatusSelector)`
  ${SStatusSelectorContainer}

`

const STagsSelector = styled(TagsSelector)`
  margin-left: -4px;
  margin-top: 5px;
  
  &.tags-selector-container {
    margin-left: -16px;
    padding: 9px 14px;
    border-radius: 4px;

    &:hover {
      background: #303030;
      cursor: pointer;
    }
  }

  .ant-select-selector {
    /* background-color: transparent !important; */
    margin-left: -4px !important;
  }

  .ant-tag {
      cursor: pointer;
      border-radius: 4px;
      /* border: 1px solid rgba(255, 255, 255, 0.00); */
      background: rgba(255, 255, 255, 0.12);
      height: 24px;
      align-items: center;

      &:hover {
        background-color:  rgba(255, 255, 255, 0.24) !important;
      }

      &:active {
        background-color: rgba(255, 255, 255, 0.32) !important;
      }
    }
  
  &.ant-select-focused {
    .ant-select-selector {
      border-color: #763fc1 !important;
      /* background-color: transparent !important; */
    }
  }
`

const SAllowForAiAssistantBox = styled.div`
  margin-top: auto;
  display: flex;
  padding: 0 24px;
`

const SAllowForAiAssistantTextBox = styled.div`
  margin-left: 15px;
`

const SAllowForAiAssistantIcon = styled(DeploymentUnitOutlined)`
  font-size: 24px;
  color: #FFFFFF73;
`

const SAllowForAiAssistantTitle = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #FFFFFFD9;
`

const SAllowForAiAssistantValue = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: #FFFFFFA6;
`

const SAllowForAiAssistantSwitch = styled(Switch)`
  margin-left: auto;

  &.ant-switch-checked {
    background-color: var(--ant-color-primary-hover);
  }
`
